import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, ChevronRight, Mail, Phone, Globe, MapPin, ArrowUp, ShieldCheck, Info } from "lucide-react";
import Seo from "@/components/common/Seo";
import { COMPANY_INFO } from "@/data/legal";

const RELATED = [
  { slug: "privacy-policy", label: "Privacy Policy" },
  { slug: "cookie-policy", label: "Cookie Policy" },
  { slug: "terms-and-conditions", label: "Terms & Conditions" },
  { slug: "delivery-return-policy", label: "Delivery & Return Policy" },
];

const ContactCard = () => (
  <div className="mt-3 rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-5">
    <p className="text-[14px] font-semibold text-white">{COMPANY_INFO.name}</p>
    <ul className="mt-3 space-y-2.5 text-[14px] text-white/65">
      <li className="flex items-start gap-2.5">
        <MapPin size={16} className="mt-0.5 shrink-0 text-[#9CA3AF]" />
        <span>{COMPANY_INFO.address}</span>
      </li>
      <li className="flex items-center gap-2.5">
        <Mail size={16} className="shrink-0 text-[#9CA3AF]" />
        <a href={`mailto:${COMPANY_INFO.email}`} className="transition-colors hover:text-white">{COMPANY_INFO.email}</a>
      </li>
      <li className="flex items-center gap-2.5">
        <Phone size={16} className="shrink-0 text-[#9CA3AF]" />
        <a href={`tel:${COMPANY_INFO.phone.replace(/\s/g, "")}`} className="transition-colors hover:text-white">{COMPANY_INFO.phone}</a>
      </li>
      <li className="flex items-center gap-2.5">
        <Globe size={16} className="shrink-0 text-[#9CA3AF]" />
        <span>{COMPANY_INFO.website}</span>
      </li>
    </ul>
  </div>
);

const Block = ({ block }) => {
  if (typeof block === "string") {
    return <p className="mt-3 text-[15px] leading-[1.75] text-white/65">{block}</p>;
  }
  if (block.sub) {
    return <h3 className="mt-7 font-display text-[18px] font-semibold tracking-[-0.01em] text-white">{block.sub}</h3>;
  }
  if (block.label) {
    return <p className="mt-5 text-[12px] font-semibold uppercase tracking-[0.16em] text-[#9CA3AF]">{block.label}</p>;
  }
  if (block.list) {
    return (
      <ul className="mt-3 space-y-2">
        {block.list.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[15px] leading-[1.65] text-white/65">
            <ChevronRight size={16} className="mt-1 shrink-0 text-[#E11B22]" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.note) {
    return (
      <div className="mt-4 flex items-start gap-3 rounded-[14px] border border-[#E11B22]/25 bg-[#E11B22]/[0.07] p-4">
        <Info size={18} className="mt-0.5 shrink-0 text-[#E11B22]" />
        <p className="text-[14px] leading-[1.7] text-white/75">{block.note}</p>
      </div>
    );
  }
  if (block.strong) {
    return (
      <div className="mt-4">
        <span className="inline-flex items-center rounded-[10px] border border-[color:var(--acx-border)] bg-white/[0.04] px-4 py-2 font-display text-[17px] font-bold tracking-[-0.01em] text-white">
          {block.strong}
        </span>
      </div>
    );
  }
  if (block.contact) {
    return <ContactCard />;
  }
  return null;
};

export default function LegalPage({ doc }) {
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState(doc?.sections?.[0]?.id || "");
  const [showTop, setShowTop] = useState(false);
  const sectionRefs = useRef({});

  const sections = useMemo(() => doc?.sections || [], [doc]);

  // Scroll progress + active section + back-to-top visibility
  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? Math.min(100, (scrollTop / docHeight) * 100) : 0);
      setShowTop(scrollTop > 600);

      const offset = 160;
      let current = sections[0]?.id || "";
      for (const s of sections) {
        const el = sectionRefs.current[s.id];
        if (el && el.getBoundingClientRect().top <= offset) {
          current = s.id;
        }
      }
      setActiveId(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections]);

  const scrollTo = useCallback((id) => {
    const el = sectionRefs.current[id];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, []);

  // Reset to top on document change
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [doc?.slug]);

  if (!doc) return null;

  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo title={doc.metaTitle} description={doc.metaDescription} />

      {/* Scroll progress bar */}
      <div className="fixed inset-x-0 top-0 z-[60] h-[3px] bg-transparent" aria-hidden="true">
        <div
          className="h-full bg-[#E11B22] transition-[width] duration-150 ease-out"
          style={{ width: `${progress}%` }}
          data-testid="legal-scroll-progress"
        />
      </div>

      {/* Hero */}
      <section className="relative acx-noise overflow-hidden border-b border-[color:var(--acx-border)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(800px_circle_at_25%_0%,rgba(156,163,175,0.10),transparent_55%),radial-gradient(640px_circle_at_85%_10%,rgba(225,27,34,0.10),transparent_55%)]" />
        <div className="acx-container relative px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <nav className="flex items-center gap-2 text-[13px] text-white/45" aria-label="Breadcrumb">
              <Link to="/" className="transition-colors hover:text-white">Home</Link>
              <ChevronRight size={14} />
              <span className="text-white/70">Legal</span>
            </nav>
            <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-[color:var(--acx-border)] bg-white/5 px-3.5 py-1.5 text-[12px] uppercase tracking-[0.18em] text-white/55">
              <ShieldCheck size={14} className="text-[#E11B22]" /> Legal
            </span>
            <h1 className="mt-4 max-w-3xl font-display text-[34px] font-extrabold leading-[1.06] tracking-[-0.03em] sm:text-5xl lg:text-6xl">
              {doc.title}
            </h1>
            <p className="mt-3 text-[14px] text-white/45">Last updated: {doc.updated}</p>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-[var(--acx-bg)]">
        <div className="acx-container px-4 py-12 sm:px-6 lg:px-10 lg:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[280px_1fr]">
            {/* Sticky TOC */}
            <aside className="hidden lg:block">
              <div className="sticky top-28">
                <p className="mb-4 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-white/50">
                  <FileText size={15} /> On this page
                </p>
                <nav className="relative max-h-[calc(100vh-12rem)] overflow-y-auto pr-2" data-testid="legal-toc">
                  <ul className="space-y-1 border-l border-[color:var(--acx-border)]">
                    {sections.map((s) => {
                      const active = activeId === s.id;
                      return (
                        <li key={s.id} className="relative">
                          {active && (
                            <motion.span
                              layoutId={`toc-active-${doc.slug}`}
                              className="absolute -left-px top-0 h-full w-[2px] bg-[#E11B22]"
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => scrollTo(s.id)}
                            data-testid={`toc-link-${s.id}`}
                            className={`block w-full border-l-2 border-transparent py-1.5 pl-4 pr-2 text-left text-[13px] leading-snug transition-colors ${
                              active ? "text-white" : "text-white/50 hover:text-white/80"
                            }`}
                          >
                            {s.title}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </div>
            </aside>

            {/* Content */}
            <article className="min-w-0 max-w-3xl">
              {/* Intro */}
              {doc.intro?.length > 0 && (
                <div className="mb-10 rounded-[18px] border border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] p-6 sm:p-7">
                  {doc.intro.map((p, i) => (
                    <p key={i} className={`text-[15px] leading-[1.75] text-white/70 ${i > 0 ? "mt-3" : ""}`}>{p}</p>
                  ))}
                </div>
              )}

              {/* Mobile TOC */}
              <div className="mb-10 lg:hidden">
                <details className="rounded-[14px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-4" data-testid="legal-toc-mobile">
                  <summary className="cursor-pointer list-none text-[13px] font-semibold uppercase tracking-[0.14em] text-white/70">
                    On this page
                  </summary>
                  <ul className="mt-3 space-y-2">
                    {sections.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => scrollTo(s.id)}
                          className="text-left text-[14px] text-white/55 transition-colors hover:text-white"
                        >
                          {s.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>

              {/* Sections */}
              {sections.map((s) => (
                <section
                  key={s.id}
                  id={s.id}
                  ref={(el) => { sectionRefs.current[s.id] = el; }}
                  className="scroll-mt-28 border-t border-[color:var(--acx-border)] py-8 first:border-t-0 first:pt-0"
                  data-testid={`legal-section-${s.id}`}
                >
                  <h2 className="font-display text-[22px] font-bold tracking-[-0.02em] text-white sm:text-[26px]">{s.title}</h2>
                  {s.blocks.map((block, i) => (
                    <Block key={i} block={block} />
                  ))}
                </section>
              ))}

              {/* Related policies */}
              <div className="mt-12 border-t border-[color:var(--acx-border)] pt-8">
                <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-white/50">Related policies</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {RELATED.filter((r) => r.slug !== doc.slug).map((r) => (
                    <Link
                      key={r.slug}
                      to={`/${r.slug}`}
                      data-testid={`related-${r.slug}`}
                      className="inline-flex items-center gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] px-4 py-2.5 text-[14px] text-white/75 transition-colors hover:border-white/25 hover:text-white"
                    >
                      {r.label} <ChevronRight size={15} />
                    </Link>
                  ))}
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Back to top */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        data-testid="legal-back-to-top"
        aria-label="Back to top"
        className={`fixed bottom-6 right-6 z-50 inline-flex h-11 w-11 items-center justify-center rounded-full border border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white shadow-[var(--acx-shadow-lift)] transition-opacity duration-200 hover:bg-white/10 ${
          showTop ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <ArrowUp size={18} />
      </button>
    </div>
  );
}
